import _ from 'underscore';
import queryString from 'query-string';
import stringify from 'json-stable-stringify';
import {
  createRequestContract,
  createResponseContract,

  requestVerbs,
  responseTypes,
  FetchPolicy,
} from '@bingads-webui-react/overreact';
import type { RequestFactoryContext } from '@bingads-webui-react/overreact';

import { schema } from '../../../environment/schema';
import { getDefaultOptions } from '../../../request-contract-helper/index';
import type {
  SignUpPayload,
  SignUpResult,
  SignUpVariables,
} from '../../../types';

function ccUrlFactory(params: RequestFactoryContext<SignUpVariables, SignUpPayload | null>) {
  const { variables } = params;

  const {
    isMca,
    isUp,
    noupsw,
    isExpertStandalone,
    isSubscriptionAds,
    isStreamlined,
    isPMaxLite,
    isChinaSignup,
    isFullyManagedFacebookAdAccountEnabled,
    isBingAdsOptional,
    authType,
    isSwitchedFromSmart,
  } = variables;

  let search: Record<string, string | boolean | undefined> = {};
  let prefix = '';
  const suffix = [];

  if (isMca) {
    search = { isMca: true };
  } else if (isSubscriptionAds) {
    prefix = 'subscription/';
  } else if (isUp) {
    search = { upse: true, noupsw };
    prefix = 'enhanced/';

    if (isFullyManagedFacebookAdAccountEnabled) {
      // if mfba=true, customer will be created with ManagedFacebookAdAccount DB pilot set to true

      search.mfba = 'true';
    }
    if (isBingAdsOptional) {
      suffix.push('bing-opt-out');
    }
  } else if (isChinaSignup) {
    prefix = 'china/';
  } else if (isExpertStandalone) {
    prefix = 'expert/';
  } else if (isStreamlined) {
    // For streamlined signup, we'll create ESC account by default
    // If we do need to create expert account, we'll pass search.isExpert='true'
    prefix = 'unified/';
  } else if (isPMaxLite) {
    prefix = 'PMaxLite/';
  } else if (isSwitchedFromSmart) {
    prefix = 'smart/';
  }

  if (!_.isEmpty(authType)) {
    search.idp = authType;
  }
  const searchString = _.isEmpty(search) ? '' : `?${queryString.stringify(search)}`;
  const suffixString = _.isEmpty(suffix) ? '' : suffix.join('&');

  const searchAndSuffixString = searchString ? `${searchString}&${suffixString}` : `?${suffixString}`;

  return `${prefix}Signup/CustomerAccountUserSignup${searchAndSuffixString}`;
}


function ccOptionsFactory(params: RequestFactoryContext<SignUpVariables, SignUpPayload | null>) {
  const { data } = params;
  const defaultOptions = getDefaultOptions();
  return {
    ...defaultOptions,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    contentType: 'application/x-www-form-urlencoded',
    data,
  };
}

export const fetchRequestContract = createRequestContract<
  SignUpVariables,
  SignUpPayload | null
>({
  schema,
  dataPath: 'sign_up',
  verb: requestVerbs.POST,
  fetchPolicy: FetchPolicy.NetworkOnly,
  uriFactoryFn: ccUrlFactory,

  headerFactoryFn: ccOptionsFactory,
  parentKeySelector: variables => stringify(variables),
});

export const fetchResponseContract = createResponseContract<
  SignUpVariables,
  SignUpPayload | null,
  SignUpResult,
  SignUpResult,
  SignUpResult
>({
  requestContract: fetchRequestContract,
  responseType: responseTypes.ENTITY,
  keySelector: entity => stringify(entity),
  processorFn: response => response,
});
